import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import Activity from '@/models/Activity';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import deleteQuestion from '@/utils/questions/deleteQuestion';
type UseQuestionsProps = {
  activity: Activity;
  cacheManager: ListCacheManager<Activity>;
};

const useQuestions = ({
  activity,
  cacheManager,
}: UseQuestionsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab',
  });

  const questions = activity.questions ?? [];

  type DeleteArgs = {
    identifier: string;
    shouldShowAlert?: boolean;
  };

  const deleteMutation = useMutation({
    mutationFn: ({ identifier }: DeleteArgs) => {
      const question = activity.questions?.find(
        q => q.identifier === identifier,
      );
      if (!question) throw new Error('Question not found');
      return deleteQuestion(question);
    },
    onSuccess: (_, { identifier, shouldShowAlert }) => {
      const filtered = activity.questions?.filter(
        q => q.identifier !== identifier,
      );
      cacheManager.update({ ...activity, questions: filtered });
      if (shouldShowAlert) alert.success(t('deleteQuestionSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    questions,
    cacheManager,
    deleteQuestion: deleteMutation.mutate,
    deleteQuestionAsync: deleteMutation.mutateAsync,
    isDeleting: deleteMutation.isPending,
  };
};

export default useQuestions;
